/**
 * The point each place's forecast model list was last fetched for, by place
 * hash, for the session: shared by `useLocationForecast` (a place's screen)
 * and `useMissingPlaceForecastModels` (the settings screens, the My weather
 * list).
 *
 * The coordinates matter for the GPS entry: it is stored under a constant
 * 'userLocation' hash, so when the user travels the hash stays the same
 * while lat/lon change, and a list fetched for the previous point would keep
 * offering its models (AROME while in Indonesia). Compared exactly: the GPS
 * point is rounded to 3 decimals at both of its sources and read once per
 * fix, so jitter never moves it (see useLocationForecast).
 */
export type ModelListFetch = {at: number; lat: number; lon: number};

const fetches = new Map<string, ModelListFetch>();

export const recordModelListFetch = (
  hash: string,
  lat: number,
  lon: number,
) => {
  fetches.set(hash, {at: Date.now(), lat, lon});
};

export const lastModelListFetch = (hash: string): ModelListFetch | undefined =>
  fetches.get(hash);

/** Whether the list held for `hash` was fetched for this very point. */
export const isModelListFetchedAt = (
  hash: string,
  lat: number,
  lon: number,
): boolean => {
  const last = fetches.get(hash);
  return last !== undefined && last.lat === lat && last.lon === lon;
};

/** Forget every fetch (tests). */
export const clearModelListFetches = () => {
  fetches.clear();
};
